import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, logAuditEvent } from "../../api/api-hook";
import { getAdminSession, setAdminSession } from "../../utils/adminAuth";

const BRAND_GREEN = "#023822";
const BRAND_GOLD = "#C9A24D";

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already logged in? Skip straight to dashboard
  useEffect(() => {
    if (getAdminSession()) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const raw = await adminLogin(email, undefined);
      const data = JSON.parse(raw || "{}");

      if (data.status === "code_sent") {
        setStep("code");
      } else {
        setError(data.error || data.message || "Not authorized. Please check your email.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const raw = await adminLogin(email, code);
      const data = JSON.parse(raw || "{}");

      if (data.status === "authenticated") {
        const displayName: string = data.displayName || email;
        setAdminSession(data.contactId, data.ownerId ?? null, displayName);
        logAuditEvent({
          actor:   displayName,
          action:  "login",
          label:   "Admin Login",
          details: `Logged in as ${email}`,
        });
        navigate("/admin/dashboard", { replace: true });
      } else {
        setError(data.error || data.message || "Invalid or expired code. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setCode("");
    setError(null);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
      }}
    >
      <div style={{ width: "100%", maxWidth: 420, padding: "1rem" }}>
        {/* Logo / brand */}
        <div className="text-center mb-4">
          <img
            src="/assets/vsga-logo.png"
            alt="VSGA"
            style={{ height: 80, width: 80, objectFit: "contain", marginBottom: "0.75rem" }}
          />
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: BRAND_GREEN,
            }}
          >
            VSGA Admin Portal
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div
            className="card-header text-center"
            style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none" }}
          >
            <span style={{ fontSize: "0.85rem", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {step === "email" ? "Sign In" : "Enter Access Code"}
            </span>
          </div>

          <div className="card-body p-4">
            {step === "email" ? (
              <>
                <p className="text-muted text-center small mb-4">
                  Enter your admin email address. A 6-digit access code will be sent to your email.
                </p>

                <form onSubmit={handleRequestCode}>
                  <div className="mb-3">
                    <label htmlFor="admin-email" className="form-label fw-semibold small">
                      Email Address
                    </label>
                    <input
                      id="admin-email"
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="alert alert-danger py-2 small text-center">{error}</div>
                  )}

                  <button
                    type="submit"
                    className="btn w-100 mt-2"
                    disabled={loading || !email.trim()}
                    style={{
                      backgroundColor: BRAND_GOLD,
                      color: "#fff",
                      border: "none",
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {loading ? "Sending…" : "Send Access Code"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <p className="text-muted text-center small mb-1">
                  A 6-digit code was sent to
                </p>
                <p className="text-center fw-semibold small mb-4" style={{ color: BRAND_GREEN }}>
                  {email}
                </p>

                <form onSubmit={handleVerifyCode}>
                  <div className="mb-3">
                    <label htmlFor="admin-code" className="form-label fw-semibold small">
                      6-Digit Access Code
                    </label>
                    <input
                      id="admin-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className="form-control text-center"
                      style={{ fontSize: "1.4rem", letterSpacing: "0.4em" }}
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="——————"
                      autoFocus
                      required
                    />
                  </div>

                  {error && (
                    <div className="alert alert-danger py-2 small text-center">{error}</div>
                  )}

                  <button
                    type="submit"
                    className="btn w-100 mt-2"
                    disabled={loading || code.length !== 6}
                    style={{
                      backgroundColor: BRAND_GOLD,
                      color: "#fff",
                      border: "none",
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {loading ? "Verifying…" : "Sign In"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-link w-100 mt-2 small text-muted text-decoration-none"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    ← Use a different email
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-muted mt-3" style={{ fontSize: "0.75rem" }}>
          Access codes expire after 24 hours. Sessions expire after 24 hours.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
